import { create } from 'zustand'

/**
 * Painel filter state (P1-impl Phase 5 Task 5.3).
 *
 * Cascading rules (per painel design §A):
 *   - grupoEconomicoId change → reset all downstream (cnpj, workspaceId, operadora, produto)
 *   - cnpj change → reset workspace/operadora/produto
 *   - workspaceId change → reset operadora/produto to match workspace's rule
 *
 * Period is independent and applies to time-ranged endpoints (scorecard, movements,
 * addressability-funnel). When undefined, backend falls back to its canonical default.
 *
 * Sharing across mount points (intentional): PainelGovernanca is rendered in two
 * places — Dashboard "Visão Executiva de Governança" tab + standalone /governance/panel
 * route. Because this store is module-level, both mounts share the same workspace +
 * period selection. That's the desired UX — switching workspaces in the tab carries
 * over to the standalone route and vice-versa.
 */
export interface IGovernancePeriod {
  start?: string // ISO date (YYYY-MM-DD)
  end?: string   // ISO date
}

export interface IGovernanceFilters {
  grupoEconomicoId?: string
  cnpj: string[]
  workspaceIds: string[]
  operadoras: string[]
  produtos: string[]
  period: IGovernancePeriod
}

export interface IGovernanceFilterActions {
  setGrupoEconomico(id?: string): void
  setCnpj(cnpj: string[]): void
  setWorkspaces(ids: string[]): void
  setOperadoras(vals: string[]): void
  setProdutos(vals: string[]): void
  setPeriod(period: IGovernancePeriod): void
  reset(): void
}

const INITIAL: IGovernanceFilters = {
  grupoEconomicoId: undefined,
  cnpj: [],
  workspaceIds: [],
  operadoras: [],
  produtos: [],
  period: {},
}

export const useGovernanceFilters = create<IGovernanceFilters & IGovernanceFilterActions>(set => ({
  ...INITIAL,

  setGrupoEconomico: (id?: string) => set({
    grupoEconomicoId: id,
    cnpj: [],
    workspaceIds: [],
    operadoras: [],
    produtos: [],
  }),

  setCnpj: (cnpj: string[]) => set({
    cnpj,
    workspaceIds: [],
    operadoras: [],
    produtos: [],
  }),

  setWorkspaces: (workspaceIds: string[]) => set({
    workspaceIds,
    operadoras: [],
    produtos: [],
  }),

  setOperadoras: (operadoras: string[]) => set({ operadoras }),
  setProdutos: (produtos: string[]) => set({ produtos }),
  setPeriod: (period: IGovernancePeriod) => set({ period }),

  reset: () => set({ ...INITIAL }),
}))

/**
 * Serialize filters to URL search params. Call inside the painel page
 * after every state change to keep the URL shareable.
 *
 * Deliberately kept string-based (not zustand middleware) because the
 * frontend uses react-router v5 — `history.replace({ search })` from a
 * hook in the page component is simpler than wiring a subscription.
 */
export function filtersToSearchParams(f: IGovernanceFilters): string {
  const p = new URLSearchParams()
  if (f.grupoEconomicoId) p.set('grupo', f.grupoEconomicoId)
  if (f.cnpj.length) p.set('cnpj', f.cnpj.join(','))
  if (f.workspaceIds.length) p.set('ws', f.workspaceIds.join(','))
  if (f.operadoras.length) p.set('op', f.operadoras.join(','))
  if (f.produtos.length) p.set('pr', f.produtos.join(','))
  if (f.period.start) p.set('start', f.period.start)
  if (f.period.end) p.set('end', f.period.end)
  const s = p.toString()
  return s ? `?${s}` : ''
}

export function filtersFromSearchParams(search: string): Partial<IGovernanceFilters> {
  const p = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search)
  const result: Partial<IGovernanceFilters> = {}
  const grupo = p.get('grupo')
  if (grupo) result.grupoEconomicoId = grupo
  const cnpj = p.get('cnpj')
  if (cnpj) result.cnpj = cnpj.split(',').filter(Boolean)
  const ws = p.get('ws')
  if (ws) result.workspaceIds = ws.split(',').filter(Boolean)
  const op = p.get('op')
  if (op) result.operadoras = op.split(',').filter(Boolean)
  const pr = p.get('pr')
  if (pr) result.produtos = pr.split(',').filter(Boolean)
  const start = p.get('start') ?? undefined
  const end = p.get('end') ?? undefined
  if (start || end) result.period = { start, end }
  return result
}
